import { useEffect, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Button, Dialog, Input, RadioGroup } from '@/design-system';
import { AnnouncementRichTextEditor } from './AnnouncementRichTextEditor';
import { CompanyMultiSelect } from './CompanyMultiSelect';
import type {
  Announcement,
  AnnouncementAudience,
  CreateAnnouncementInput,
  UpdateAnnouncementInput,
} from '../types';

type Props = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  announcement: Announcement | null;
  onClose: () => void;
  mutations: {
    createAnnouncement: UseMutationResult<Announcement, Error, CreateAnnouncementInput>;
    updateAnnouncement: UseMutationResult<
      Announcement,
      Error,
      { id: string; data: UpdateAnnouncementInput }
    >;
    uploadMedia: UseMutationResult<{ url: string }, Error, File>;
  };
};

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

export function AnnouncementEditorDialog({
  isOpen,
  mode,
  announcement,
  onClose,
  mutations,
}: Props) {
  const { createAnnouncement, updateAnnouncement, uploadMedia } = mutations;
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [audience, setAudience] = useState<AnnouncementAudience>('all');
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const audienceFrozen =
    mode === 'edit' &&
    (announcement?.status === 'published' || announcement?.status === 'archived');

  useEffect(() => {
    // Ao fechar, limpa o form para o próximo "Novo" não herdar body/título
    if (!isOpen) {
      setTitle('');
      setBodyHtml('');
      setAudience('all');
      setCompanyIds([]);
      setErrors({});
      return;
    }
    if (mode === 'create') {
      setTitle('');
      setBodyHtml('');
      setAudience('all');
      setCompanyIds([]);
      setErrors({});
      return;
    }
    if (announcement) {
      setTitle(announcement.title);
      setBodyHtml(announcement.bodyHtml);
      setAudience(announcement.audience);
      setCompanyIds(announcement.companyIds ?? []);
      setErrors({});
    }
  }, [isOpen, mode, announcement]);

  const isSubmitting = createAnnouncement.isPending || updateAnnouncement.isPending;

  const validate = () => {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = 'Título obrigatório';
    if (!stripHtml(bodyHtml)) next.bodyHtml = 'Conteúdo obrigatório';
    if (audience === 'selected' && companyIds.length === 0) {
      next.companyIds = 'Selecione ao menos uma empresa';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      if (mode === 'create') {
        await createAnnouncement.mutateAsync({
          title: title.trim(),
          bodyHtml,
          audience,
          companyIds: audience === 'selected' ? companyIds : [],
        });
      } else if (announcement) {
        const data: UpdateAnnouncementInput = {
          title: title.trim(),
          bodyHtml,
        };
        if (!audienceFrozen) {
          data.audience = audience;
          data.companyIds = audience === 'selected' ? companyIds : [];
        }
        await updateAnnouncement.mutateAsync({ id: announcement.id, data });
      }
      onClose();
    } catch {
      // toast handled by mutation
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Novo comunicado' : 'Editar comunicado'}
      size="xl"
      footer={(
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
          <Button variant="primary" onClick={() => void handleSubmit()} isLoading={isSubmitting}>
            {mode === 'create' ? 'Salvar rascunho' : 'Salvar'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-5">
        <Input
          label="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={errors.title}
          required
          disabled={isSubmitting}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Mensagem</label>
          <AnnouncementRichTextEditor
            key={
              isOpen
                ? mode === 'create'
                  ? 'announcement-create'
                  : `announcement-edit-${announcement?.id ?? 'unknown'}`
                : 'announcement-closed'
            }
            value={bodyHtml}
            onChange={setBodyHtml}
            disabled={isSubmitting}
            onUploadImage={async (file) => {
              try {
                const res = await uploadMedia.mutateAsync(file);
                return res.url;
              } catch (err) {
                // toast handled by mutation onError; rethrow so editor skips insert
                throw err instanceof Error ? err : new Error('Falha ao enviar imagem');
              }
            }}
          />
          {errors.bodyHtml && <p className="mt-1 text-xs text-red-600">{errors.bodyHtml}</p>}
        </div>

        <div className="space-y-2">
          {audienceFrozen && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
              Após publicar, a audiência não pode ser alterada.
            </p>
          )}
          <RadioGroup
            label="Destinatários"
            value={audience}
            onChange={(value) => setAudience(value as AnnouncementAudience)}
            disabled={audienceFrozen || isSubmitting}
            options={[
              { value: 'all', label: 'Todos os clientes' },
              { value: 'selected', label: 'Clientes específicos' },
            ]}
          />
          <input type="hidden" name="audience" value="on" disabled={audienceFrozen || isSubmitting} />
          {audience === 'selected' && (
            <CompanyMultiSelect
              selectedIds={companyIds}
              onChange={setCompanyIds}
              disabled={audienceFrozen || isSubmitting}
            />
          )}
          {errors.companyIds && <p className="text-xs text-red-600">{errors.companyIds}</p>}
        </div>

      </div>
    </Dialog>
  );
}
